import { computed, inject } from "@angular/core";
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { Observable, catchError, map, of, pipe, switchMap, tap } from "rxjs";
import {
  ContentElementClient,
  ContentElementDto,
  CtaDto,
} from "./coremedia-content.client";
import {
  LinkResolverService,
  ResolvedLink,
} from "../../navigation/data/link-resolver.service";

interface ContentElementState {
  id: string | null;
  viewType: string | null;
  status: "idle" | "loading" | "ready" | "error";
  element: ContentElementDto | null;
  error: unknown;
}

const initialState: ContentElementState = {
  id: null,
  viewType: null,
  status: "idle",
  element: null,
  error: null,
};

export const contentDetailStore = signalStore(
  withState(initialState),
  withComputed((store) => ({
    isReady: computed(() => store.status() === "ready"),
    isVideo: computed(() => store.element()?.type === "CMVideo"),
    firstImage: computed(() => store.element()?.images?.[0] ?? null),
  })),
  withMethods(
    (
      store,
      client = inject(ContentElementClient),
      linkResolver = inject(LinkResolverService),
    ) => ({
      loadElement: rxMethod<{ id: string; viewType: string; rootName: string }>(
        pipe(
          tap(({ id, viewType }) =>
            patchState(store, {
              id,
              viewType,
              status: "loading",
              error: null,
            }),
          ),
          switchMap(({ id, viewType, rootName }) =>
            client.loadElement(id, viewType, rootName).pipe(
              switchMap((element) => enrichCtaTargets(element, linkResolver)),
              tap((element) => patchState(store, { element, status: "ready" })),
              catchError((error) => {
                patchState(store, { status: "error", error, element: null });
                return of(null);
              }),
            ),
          ),
        ),
      ),
    }),
  ),
);

function ctasToResolve(element: ContentElementDto): CtaDto[] {
  return [
    ...(element.ctas ?? []).filter(
      (c) => c.targetId != null && c.targetUrl == null,
    ),
    ...(element.children ?? []).flatMap(ctasToResolve),
  ];
}

function enrichCtaTargets(
  element: ContentElementDto,
  resolver: LinkResolverService,
): Observable<ContentElementDto> {
  const pending = ctasToResolve(element);
  if (pending.length === 0) {
    return of(element);
  }

  const ids = [
    ...new Set(
      pending
        .filter((c): c is CtaDto & { targetId: string } => c.targetId != null)
        .map((c) => parseInt(c.targetId, 10)),
    ),
  ];
  return resolver
    .resolveLinks(ids)
    .pipe(map((resolved) => withResolvedCtaTargets(element, resolved)));
}

function withResolvedCtaTargets(
  element: ContentElementDto,
  resolved: ReadonlyMap<number, ResolvedLink>,
): ContentElementDto {
  return {
    ...element,
    ctas: (element.ctas ?? []).map((cta) => {
      if (cta.targetId == null || cta.targetUrl != null) {
        return cta;
      }
      const link = resolved.get(parseInt(cta.targetId, 10));
      const resolvedUrl = link?.resolved ? link.route : undefined;
      return resolvedUrl ? { ...cta, targetUrl: resolvedUrl } : cta;
    }),
    children: (element.children ?? []).map((child) =>
      withResolvedCtaTargets(child, resolved),
    ),
  };
}
